import * as React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSession } from "@/features/auth/hooks/useSession";
import {
  useConversation,
  getLastViewedConversation,
} from "./providers/ConversationContext";
import {
  buildConversationPath,
  resolveWorkspaceId,
} from "@/lib/workspace-routing";
import { shouldRedirectToFirstConversation } from "./conversationRouteSync";
import { isWorkspaceDefaultConversation } from "@/types/conversations";

/**
 * Conversation Route Sync - keeps URL params and in-memory conversation state aligned.
 */
export function ConversationRouteSync({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    workspaceId: workspaceIdParam,
    conversationId,
    view,
    moduleId,
  } = useParams<{
    workspaceId?: string;
    conversationId?: string;
    view?: string;
    moduleId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    activeConversation,
    setActiveConversation,
    allConversations,
    isLoading,
  } = useConversation();
  const { selectedContainer } = useSession();
  const workspaceId = React.useMemo(
    () => workspaceIdParam || resolveWorkspaceId(selectedContainer),
    [workspaceIdParam, selectedContainer?.id, selectedContainer?.ownerId],
  );

  const lastSyncedConversationId = React.useRef<string | null>(null);
  // Track which direction triggered the sync to prevent ping-pong
  const syncSource = React.useRef<"url" | "state" | null>(null);

  // Unified sync effect — handles both URL→State and State→URL
  React.useEffect(() => {
    if (isLoading || allConversations.length === 0) return;

    // The settings route is intentionally conversation-less. Without this guard
    // the State→URL branch below immediately redirects /settings back to a
    // conversation URL (it has no :conversationId), so Workspace Settings could
    // never stay open.
    if (location.pathname === "/settings") return;

    const searchSuffix = location.search;
    const buildPathForConversation = (targetConversationId: string) => {
      if (moduleId) {
        return buildConversationPath(workspaceId, targetConversationId, {
          moduleId,
          search: searchSuffix,
        });
      }
      return buildConversationPath(workspaceId, targetConversationId, {
        view: view === "chat" ? "chat" : "workspace",
        search: searchSuffix,
      });
    };

    // URL → State sync: URL has conversation ID that doesn't match state
    if (conversationId && conversationId !== activeConversation?.id) {
      // Skip if we just synced this from state (prevents ping-pong)
      if (
        syncSource.current === "state" &&
        lastSyncedConversationId.current === conversationId
      ) {
        syncSource.current = null;
        return;
      }

      const targetConversation = allConversations.find(
        (item) => item.id === conversationId,
      );
      if (targetConversation) {
        syncSource.current = "url";
        lastSyncedConversationId.current = conversationId;
        setActiveConversation(targetConversation);
        return;
      } else if (
        shouldRedirectToFirstConversation(conversationId, allConversations)
      ) {
        // Genuinely-missing real id (stale/deleted) - redirect to first
        // available conversation in current workspace.
        navigate(buildPathForConversation(allConversations[0].id), {
          replace: true,
        });
        return;
      } else {
        // Optimistic temp id (`conversation-*`) the new-chat flow just stamped
        // into the URL. Do NOT bounce it back to the first conversation (#4385):
        // ConversationContext.createConversation's onSuccess navigates to the
        // real id once the server responds. No-op until then.
        return;
      }
    }

    // Only sync State→URL when the URL has NO conversationId — keeps the URL the
    // single source of truth and prevents redirect loops.
    if (!conversationId) {
      const selectedWorkspaceId =
        selectedContainer?.id ?? selectedContainer?.ownerId;
      const workspaceDefaultConversation = allConversations.find((item) =>
        isWorkspaceDefaultConversation(item),
      );
      const lastConversationId = selectedWorkspaceId
        ? getLastViewedConversation(selectedWorkspaceId)
        : null;
      const lastConversation = lastConversationId
        ? allConversations.find((item) => item.id === lastConversationId)
        : null;
      const targetConversation =
        lastConversation ??
        workspaceDefaultConversation ??
        activeConversation ??
        allConversations[0];

      if (targetConversation) {
        // #1873: URL is the canonical source for the active module. We only
        // reach this branch when the URL has no conversation (hard refresh on
        // `/` or first load), so it has no module either — storage fills in
        // *only because the URL is empty*; a `/mod/<id>` URL would skip this
        // branch and MainLayout would persist URL → storage instead. We seed
        // `rememberedModuleId` from storage so the first navigate emits the
        // final URL (no replace() flash), scoped as an empty-URL-only fallback
        // that never overrides a URL value.
        const rememberedModuleId = (() => {
          try {
            return (
              localStorage.getItem(`lastModule:${targetConversation.id}`) ||
              sessionStorage.getItem(`lastModule:${targetConversation.id}`)
            );
          } catch {
            try {
              return sessionStorage.getItem(
                `lastModule:${targetConversation.id}`,
              );
            } catch {
              return null;
            }
          }
        })();

        syncSource.current = "state";
        lastSyncedConversationId.current = targetConversation.id;
        // URL had no moduleId — fall back to storage (which itself was
        // last written from a URL, per MainLayout's URL→storage write
        // path). If storage is empty too, drop to the no-module path and
        // let MainLayout pick the conversation's first module.
        const path = rememberedModuleId
          ? buildConversationPath(workspaceId, targetConversation.id, {
              moduleId: rememberedModuleId,
              search: searchSuffix,
            })
          : buildPathForConversation(targetConversation.id);
        navigate(path, { replace: true });
        return;
      }
    }
  }, [
    conversationId,
    moduleId,
    allConversations,
    isLoading,
    activeConversation?.id,
    setActiveConversation,
    navigate,
    view,
    workspaceId,
    activeConversation,
    location.search,
    location.pathname,
    selectedContainer?.id,
    selectedContainer?.ownerId,
  ]);

  return <>{children}</>;
}
